import React, { useState, useEffect, useCallback } from "react";
import { Layout, Select, Modal, Form, TimePicker, Upload, Button, Tag, Divider, Typography } from "antd";
import { UploadOutlined, CheckCircleFilled, ExclamationCircleFilled, CalendarOutlined, ClockCircleOutlined, UserOutlined, FileTextOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";


const { Content } = Layout;
const { Option } = Select;

/* ─── Paleta de Status ─────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  PRESENTE:           { bg: "#D1FAE5", color: "#065F46", dot: "#10B981", label: "Presente" },
  ATRASO:             { bg: "#FFF7ED", color: "#9A3412", dot: "#F97316", label: "Atraso" },  // 👈 novo
  FALTA:              { bg: "#FEE2E2", color: "#991B1B", dot: "#EF4444", label: "Falta" },
  "FALTA JUSTIFICADA":{ bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B", label: "Justificada" },
  "FÉRIAS":           { bg: "#DBEAFE", color: "#1E3A8A", dot: "#3B82F6", label: "Férias" },
  FERIADO:            { bg: "#EDE9FE", color: "#4C1D95", dot: "#8B5CF6", label: "Feriado" },
  "FINAL DE SEMANA":  { bg: "#F3F4F6", color: "#6B7280", dot: "#D1D5DB", label: "F. Semana" },
};

const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const DIAS  = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];

/* ─── Skeleton Loader ──────────────────────────────────────────────────────── */
const SkeletonCell = () => (
  <div style={{
    borderRadius: 10, minHeight: 72, background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
  }} />
);

const SkeletonCard = () => (
  <div style={{ background:"#fff", border:"1px solid #F3F4F6", borderRadius:14, padding:18, marginBottom:12 }}>
    {[80,120,60].map((w,i) => (
      <div key={i} style={{ height:12, width:`${w}%`, borderRadius:6, marginBottom:10,
        background:"linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
        backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
    ))}
  </div>
);

/* ─── Componente Principal ─────────────────────────────────────────────────── */
const Transacoes = () => {
  const userStored = JSON.parse(localStorage.getItem("user") || "{}");
  const employeeId = userStored.employee_id;
  const diretor    = userStored.diretor || "N";
  const nomeUser   = userStored.name || "Colaborador";
  const [faltaSelecionada, setFaltaSelecionada] = useState(null);
const [modalFaltaOpen, setModalFaltaOpen] = useState(false);

  const [tab, setTab]               = useState("calendario");
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [calData, setCalData]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [modalOpen, setModalOpen]   = useState(false);
  const [tipos, setTipos]           = useState([]);
  const [form]                      = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList]     = useState([]);

  const [faltasData, setFaltasData]       = useState([]);
  const [faltasMes, setFaltasMes]         = useState(dayjs());
  const [faltaFiltro, setFaltaFiltro]     = useState("TODOS");
  const [faltasLoading, setFaltasLoading] = useState(false);

  useEffect(() => { document.title = "Movimentações | Dashboard"; }, []);

  /* ── Calendário ── */
  const loadCalendar = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    const dataInicio = currentDate.startOf("month").format("YYYY-MM-DD");
    const dataFim    = currentDate.endOf("month").format("YYYY-MM-DD");
    try {
      const res = await Api.ListTransacao({ employee_id: employeeId, dataInicio, dataFim });
      setCalData(res?.data?.registros || []);
    } catch { toast.error("Erro ao carregar dados."); }
    finally  { setLoading(false); }
  }, [employeeId, currentDate]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const byDate = Object.fromEntries(calData.map(d => [d.data, d]));

  const stats = [
    { key: "PRESENTE",           label: "Presenças" },
      { key: "ATRASO",             label: "Atrasos" },
    { key: "FALTA",              label: "Faltas" },
    { key: "FALTA JUSTIFICADA",  label: "Justificadas" },
    { key: "FÉRIAS",             label: "Férias" },
    { key: "FERIADO",            label: "Feriados" },
  ].map(s => ({
    ...s,
    cfg: STATUS_CONFIG[s.key],
    value: calData.filter(d => d.status === s.key).length,
  }));

  /* ── Detalhe do dia ── */
  const openDay = async (dateStr) => {
    const rec = byDate[dateStr] || { data: dateStr, status: "SEM DADOS" };
    setSelectedDay(rec);
    setFileList([]);
    setModalOpen(true);
    form.resetFields();
    form.setFieldsValue({ inicio: dayjs("08:30","HH:mm"), fim: dayjs("17:30","HH:mm") });
    if (rec.status === "FALTA" && !tipos.length) {
      try { const t = await Api.ListTipos(); setTipos(t || []); }
      catch { setTipos([]); }
    }
  };

const uploadProps = {
  beforeUpload: (file) => {
    setFileList([file]); // 👈 GUARDA O FILE REAL
    return false;
  },
  onRemove: () => setFileList([]),
  fileList,
  maxCount: 1,
};


  /* ── Submeter justificação ── */
  const submitJustificacao = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    const fd = new FormData();
    fd.append("direcao", userStored.direcao);
    fd.append("employee_id", employeeId);
    fd.append("diretor",     diretor);
    fd.append("inicio",      `${selectedDay.data} ${values.inicio.format("HH:mm:ss")}`);
    fd.append("fim",         `${selectedDay.data} ${values.fim.format("HH:mm:ss")}`);
    fd.append("id_tipo",     values.id_tipo);
    if (values.obs) fd.append("obs", values.obs);
if (fileList[0]) {
  fd.append("file", fileList[0]); // 👈 AGORA É FILE REAL
}
    try {
      await Api.AddFalta(fd);
      toast.success("Justificação enviada com sucesso!");
      setModalOpen(false);
      loadCalendar();
    } catch { toast.error("Erro ao enviar justificação."); }
    finally  { setSubmitting(false); }
  };

  /* ── Faltas ── */
  const loadFaltas = useCallback(async () => {
    if (!employeeId) return;
    setFaltasLoading(true);
    const dataInicio = faltasMes.startOf("month").format("YYYY-MM-DD");
    const dataFim    = faltasMes.endOf("month").format("YYYY-MM-DD");
    try {
      const res = await Api.ListFalta({ employee_id: employeeId, dataInicio, dataFim });
      setFaltasData(Array.isArray(res) ? res : []);
    } catch { toast.error("Erro ao carregar faltas."); }
    finally  { setFaltasLoading(false); }
  }, [employeeId, faltasMes]);

  useEffect(() => { if (tab === "faltas") loadFaltas(); }, [tab, loadFaltas]);

  const faltasFiltradas = faltasData.filter(f =>
    faltaFiltro === "TODOS" ? true :
    faltaFiltro === "APROVADO" ? f.justificado === "S" : f.justificado === "N"
  );

  /* ── Render do calendário ── */
  const renderCells = () => {
    const year      = currentDate.year();
    const month     = currentDate.month();
    const firstDow  = dayjs(`${year}-${String(month+1).padStart(2,"0")}-01`).day();
    const totalDays = currentDate.daysInMonth();
    const todayStr  = dayjs().format("YYYY-MM-DD");
    const cells     = [];

    for (let i = 0; i < firstDow; i++) cells.push(<div key={`e${i}`} />);

    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const rec     = byDate[dateStr];
      const cfg     = rec ? STATUS_CONFIG[rec.status] : null;
      const isToday = dateStr === todayStr;
      const isFds   = rec?.status === "FINAL DE SEMANA";

      cells.push(
        <div
          key={dateStr}
          onClick={() => openDay(dateStr)}
          style={{
            borderRadius: 10,
            padding: "9px 8px 8px",
            minHeight: 72,
            cursor: "pointer",
            background: cfg ? cfg.bg : "#FAFAFA",
            border: isToday ? "2px solid #185FA5" : "1px solid transparent",
            opacity: isFds ? 0.55 : 1,
            transition: "all 0.18s ease",
            position: "relative",
          }}
          onMouseEnter={e => { if(!isFds) e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,0.10)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}
        >
          {/* Número do dia */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
            <span style={{
              fontSize: 13, fontWeight: isToday ? 700 : 500,
              color: isToday ? "#185FA5" : "#374151",
              background: isToday ? "#DBEAFE" : "transparent",
              borderRadius: isToday ? "50%" : 0,
              width: isToday ? 22 : "auto", height: isToday ? 22 : "auto",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{d}</span>
            {cfg && <span style={{ width:7, height:7, borderRadius:"50%", background:cfg.dot, flexShrink:0 }} />}
          </div>

          {/* Badge de status */}
          {cfg && !isFds && (
            <span style={{
              fontSize: 9, fontWeight: 600, letterSpacing:"0.3px",
              color: cfg.color, display:"block",
              textTransform:"uppercase",
            }}>{cfg.label}</span>
          )}

          {/* Horários */}
{/* Horários */}
{rec?.entrada && (
  <div style={{ marginTop:5, display:"flex", flexDirection:"column", gap:2 }}>
    <span style={{ fontSize:9, color:"#374151", fontWeight:500 }}>▶ {rec.entrada}</span>
    {rec.saida && <span style={{ fontSize:9, color:"#374151", fontWeight:500 }}>◀ {rec.saida}</span>}
  </div>
)}

{/* 👇 Adiciona isto após o bloco de horários */}
{rec?.atraso && (
  <span style={{ fontSize:8, fontWeight:600, color:"#9A3412", marginTop:3, display:"block" }}>
     +{rec.atraso}
  </span>
)}
{rec?.horas_extras && (
  <span style={{ fontSize:8, fontWeight:600, color:"#1D4ED8", marginTop:1, display:"block" }}>
    {rec.horas_extras}
  </span>
)}
        </div>
      );
    }
    return cells;
  };

  const rec    = selectedDay;
  const recCfg = rec ? STATUS_CONFIG[rec.status] : null;

  /* ─────────────────── RENDER ─────────────────── */
  return (
    <Layout style={{ background:"transparent", minHeight:"100vh" }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .trans-tab-btn { transition: all 0.2s; }
        .trans-tab-btn:hover { background: #F0F6FF !important; }
        .trans-stat-card { transition: all 0.2s; }
        .trans-stat-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important; }
        .falta-card-item { transition: all 0.18s; }
        .falta-card-item:hover { transform: translateX(3px); border-color: #BFDBFE !important; }
      `}</style>

      <Content style={{ padding:"28px 36px", animation:"fadeUp 0.4s ease" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── HEADER PRINCIPAL ─────────────────────────────────────────── */}
        <div style={{
          background:"linear-gradient(135deg, #0F4C8A 0%, #185FA5 50%, #2563EB 100%)",
          borderRadius:18, padding:"26px 32px", marginBottom:28,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          boxShadow:"0 16px 48px rgba(24,95,165,0.28)",
          position:"relative", overflow:"hidden",
        }}>
          {/* Decoração geométrica */}
          <div style={{ position:"absolute", right:-30, top:-30, width:180, height:180, borderRadius:"50%",
            background:"rgba(255,255,255,0.06)", pointerEvents:"none" }} />
          <div style={{ position:"absolute", right:60, bottom:-50, width:120, height:120, borderRadius:"50%",
            background:"rgba(255,255,255,0.04)", pointerEvents:"none" }} />

          <div style={{ position:"relative" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <div style={{ width:36, height:36, borderRadius:10,
                background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <h1 style={{ color:"#fff", fontSize:20, fontWeight:700, margin:0, letterSpacing:"-0.3px" }}>
                Movimentações
              </h1>
            </div>
            <p style={{ color:"rgba(255,255,255,0.70)", fontSize:13, margin:0 }}>
              Olá, <strong style={{color:"rgba(255,255,255,0.92)"}}>{userStored.nome}</strong>
            </p>
          </div>

          {/* Tabs no header */}
          <div style={{ display:"flex", gap:4, background:"rgba(0,0,0,0.2)", borderRadius:12, padding:4 }}>
            {[["calendario","Calendário"],["faltas","Minhas Faltas"]].map(([key,label]) => (
              <button key={key} className="trans-tab-btn" onClick={() => setTab(key)} style={{
                padding:"8px 20px", fontSize:13, cursor:"pointer",
                borderRadius:9, border:"none", fontWeight:600, letterSpacing:"0.1px",
                background: tab===key ? "#fff" : "transparent",
                color: tab===key ? "#185FA5" : "rgba(255,255,255,0.80)",
                boxShadow: tab===key ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* ══════════════ ABA CALENDÁRIO ══════════════ */}
        {tab === "calendario" && (
          <>
            {/* Navegação de mês + stats */}
            <div style={{ display:"flex", gap:16, marginBottom:24, flexWrap:"wrap", alignItems:"stretch" }}>

              {/* Controlo de mês */}
              <div style={{
                background:"#fff", borderRadius:14, padding:"16px 20px",
                border:"1px solid #E5E7EB", display:"flex", alignItems:"center",
                gap:12, flexShrink:0, boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
              }}>
                <button onClick={() => setCurrentDate(d => d.subtract(1,"month"))} style={{
                  width:34, height:34, borderRadius:8, border:"1px solid #E5E7EB",
                  background:"#F9FAFB", cursor:"pointer", fontSize:16, display:"flex",
                  alignItems:"center", justifyContent:"center", color:"#374151",
                }}>‹</button>
                <div style={{ textAlign:"center", minWidth:120 }}>
                  <div style={{ fontSize:17, fontWeight:700, color:"#111827", letterSpacing:"-0.3px" }}>
                    {MESES[currentDate.month()]}
                  </div>
                  <div style={{ fontSize:12, color:"#9CA3AF", fontWeight:500 }}>{currentDate.year()}</div>
                </div>
                <button onClick={() => setCurrentDate(d => d.add(1,"month"))} style={{
                  width:34, height:34, borderRadius:8, border:"1px solid #E5E7EB",
                  background:"#F9FAFB", cursor:"pointer", fontSize:16, display:"flex",
                  alignItems:"center", justifyContent:"center", color:"#374151",
                }}>›</button>
              </div>

              {/* Cards de estatísticas */}
              <div style={{ display:"flex", gap:10, flex:1, flexWrap:"wrap" }}>
                {stats.map(s => (
                  <div key={s.key} className="trans-stat-card" style={{
                    background:"#fff", border:"1px solid #F3F4F6", borderRadius:14,
                    padding:"14px 18px", flex:"1 1 80px", minWidth:80,
                    boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
                    borderTop:`3px solid ${s.cfg.dot}`,
                  }}>
                    <div style={{ fontSize:24, fontWeight:700, color:s.cfg.color, lineHeight:1 }}>
                      {loading ? "—" : s.value}
                    </div>
                    <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4, fontWeight:500, letterSpacing:"0.2px" }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Grid do calendário */}
            <div style={{
              background:"#fff", borderRadius:16, padding:20,
              border:"1px solid #E5E7EB", boxShadow:"0 4px 16px rgba(0,0,0,0.05)",
            }}>
              {/* Cabeçalho dos dias */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:4, marginBottom:8 }}>
                {DIAS.map((d,i) => (
                  <div key={d} style={{
                    textAlign:"center", fontSize:11, fontWeight:700, letterSpacing:"0.5px",
                    color: i===0||i===6 ? "#D1D5DB" : "#9CA3AF",
                    padding:"6px 0", textTransform:"uppercase",
                  }}>{d}</div>
                ))}
              </div>

              {/* Células */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:5 }}>
                {loading
                  ? Array.from({length:35}).map((_,i) => <SkeletonCell key={i}/>)
                  : renderCells()
                }
              </div>

              {/* Legenda */}
              <div style={{ display:"flex", gap:16, marginTop:18, paddingTop:16,
                borderTop:"1px solid #F3F4F6", flexWrap:"wrap" }}>
                {Object.entries(STATUS_CONFIG).map(([key,cfg]) => (
                  <div key={key} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <span style={{ width:8, height:8, borderRadius:"50%", background:cfg.dot, flexShrink:0 }}/>
                    <span style={{ fontSize:11, color:"#6B7280", fontWeight:500 }}>{cfg.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ══════════════ ABA FALTAS ══════════════ */}
        {tab === "faltas" && (
          <>
            {/* Filtros */}
            <div style={{
              background:"#fff", borderRadius:14, padding:"16px 20px",
              border:"1px solid #E5E7EB", marginBottom:20,
              display:"flex", gap:12, alignItems:"center", flexWrap:"wrap",
              boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
            }}>
              <div style={{ fontSize:13, fontWeight:600, color:"#374151", marginRight:4 }}>Filtrar por:</div>
              <Select value={faltaFiltro} onChange={setFaltaFiltro} style={{ width:180 }} size="middle">
                <Option value="TODOS">Todos os estados</Option>
                <Option value="APROVADO">Aprovadas</Option>
                <Option value="PENDENTE">Pendentes</Option>
              </Select>
              <input type="month"
                value={faltasMes.format("YYYY-MM")}
                onChange={e => setFaltasMes(dayjs(e.target.value))}
                style={{ padding:"6px 12px", borderRadius:8, fontSize:13,
                  border:"1px solid #D1D5DB", color:"#374151", outline:"none" }}
              />
              <div style={{ marginLeft:"auto", fontSize:12, color:"#9CA3AF" }}>
                {faltasFiltradas.length} registo{faltasFiltradas.length!==1?"s":""}
              </div>
            </div>

            {/* Lista */}
            {faltasLoading ? (
              <>{[1,2,3].map(i => <SkeletonCard key={i}/>)}</>
            ) : faltasFiltradas.length === 0 ? (
              <div style={{
                background:"#fff", borderRadius:16, padding:"48px 24px", textAlign:"center",
                border:"1px solid #E5E7EB", boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
              }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
                <div style={{ fontSize:15, fontWeight:600, color:"#374151", marginBottom:6 }}>Nenhuma falta encontrada</div>
                <div style={{ fontSize:13, color:"#9CA3AF" }}>Tente ajustar os filtros de pesquisa</div>
              </div>
            ) : faltasFiltradas.map(f => {
              const aprovado = f.justificado === "S";
              const dI = dayjs(f.inicio), dF = dayjs(f.fim);
              return (
         
<div
  key={f.id}
  className="falta-card-item"
  onClick={() => {
    setFaltaSelecionada(f);
    setModalFaltaOpen(true);
  }}
style={{
                  cursor: "pointer",
                  background:"#fff", border:"1px solid #E5E7EB", borderRadius:14,
                  padding:"18px 20px", marginBottom:10,
                  boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
                  borderLeft:`4px solid ${aprovado ? "#10B981" : "#F59E0B"}`,
                }}>



                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:3 }}>
                        {f.motivo || "—"}
                      </div>
                      <div style={{ fontSize:12, color:"#9CA3AF" }}>
                        Justificação #{f.id} &middot; {f.colaborador}
                      </div>
                    </div>
                    <span style={{
                      fontSize:11, padding:"4px 12px", borderRadius:20, fontWeight:700,
                      letterSpacing:"0.3px", textTransform:"uppercase",
                      background: aprovado ? "#D1FAE5" : "#FEF3C7",
                      color: aprovado ? "#065F46" : "#92400E",
                    }}>{aprovado ? "✓ Aprovado" : "⏳ Pendente"}</span>
                  </div>
                  <div style={{ display:"flex", gap:20, fontSize:12, color:"#6B7280", flexWrap:"wrap" }}>
                    <span style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ fontSize:13 }}>📅</span>
                      {dI.format("DD/MM/YYYY")}
                      {dI.format("DD/MM/YYYY") !== dF.format("DD/MM/YYYY") && ` → ${dF.format("DD/MM/YYYY")}`}
                    </span>
                    <span style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ fontSize:13 }}>🕐</span>
                      {dI.format("HH:mm")} – {dF.format("HH:mm")}
                    </span>
                    {aprovado && f.justificador && (
                      <span style={{ display:"flex", alignItems:"center", gap:5, color:"#065F46" }}>
                        <span>✓ {f.justificador}</span>
                        {f.data_aprovacao && <span style={{ color:"#9CA3AF" }}>em {dayjs(f.data_aprovacao).format("DD/MM/YYYY")}</span>}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* ══════════════ MODAL DETALHE ══════════════ */}
        <Modal
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          width={460}
          styles={{ header:{ borderBottom:"1px solid #F3F4F6", paddingBottom:14 } }}
          title={
            rec && (
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:38, height:38, borderRadius:10, flexShrink:0,
                  background: recCfg?.bg || "#F3F4F6",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <span style={{ width:12, height:12, borderRadius:"50%", background: recCfg?.dot || "#9CA3AF" }}/>
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>
                    {rec.dia_semana ? rec.dia_semana.charAt(0).toUpperCase()+rec.dia_semana.slice(1) : rec.data}
                  </div>
                  <div style={{ fontSize:12, color:"#9CA3AF", fontWeight:400 }}>
                    {rec.data} &middot; <span style={{ color: recCfg?.color }}>{recCfg?.label || rec.status}</span>
                  </div>
                </div>
              </div>
            )
          }
        >

          {rec && (
            <div style={{ paddingTop:4 }}>
              {/* Detalhes do dia */}
              {[
                rec.nome     && ["Colaborador", rec.nome],
                rec.entrada  && ["Entrada",      rec.entrada],
                rec.saida    && ["Saída",         rec.saida],
                rec.atraso    && ["Atraso",      <span style={{color:"#9A3412",fontWeight:700}}> {rec.atraso}</span>],          // 👈
                rec.horas_extras && ["Horas extras", <span style={{color:"#1D4ED8",fontWeight:700}}> {rec.horas_extras}</span>], // 👈
                rec.descricao&& ["Descrição",     rec.descricao],
              ].filter(Boolean).map(([label, val]) => (
                <div key={label} style={{
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"10px 0", borderBottom:"1px solid #F9FAFB",
                }}>
                  <span style={{ fontSize:13, color:"#9CA3AF", fontWeight:500 }}>{label}</span>
                  <span style={{ fontSize:13, color:"#111827", fontWeight:600 }}>{val}</span>
                </div>
              ))}

              {/* Sem acção */}
              {["FINAL DE SEMANA","FÉRIAS","FERIADO","PRESENTE","ATRASO","FALTA JUSTIFICADA"].includes(rec.status) && (
                <div style={{ marginTop:16, padding:"12px 16px", borderRadius:10,
                  background:"#F9FAFB", border:"1px solid #F3F4F6" }}>
                  <p style={{ fontSize:13, color:"#9CA3AF", margin:0, textAlign:"center" }}>
                    {rec.status === "PRESENTE" || rec.status === "ATRASO"
  ? "✓ Dia trabalhado — sem ações disponíveis"
  : "Nenhuma ação disponível para este dia."}
                  </p>
                </div>
              )}

              {/* Formulário de justificação */}
              {rec.status === "FALTA" && (
                <div style={{ marginTop:20, borderTop:"1px solid #F3F4F6", paddingTop:18 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:3, height:18, borderRadius:2, background:"#185FA5" }}/>
                    <span style={{ fontSize:14, fontWeight:700, color:"#111827" }}>Justificar esta falta</span>
                  </div>

                  <Form form={form} layout="vertical" size="middle">
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Hora início</span>}
                        name="inicio" rules={[{required:true,message:"Obrigatório"}]}>
                        <TimePicker format="HH:mm" style={{ width:"100%" }}/>
                      </Form.Item>
                      <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Hora fim</span>}
                        name="fim" rules={[{required:true,message:"Obrigatório"}]}>
                        <TimePicker format="HH:mm" style={{ width:"100%" }}/>
                      </Form.Item>
                    </div>

                    <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Motivo</span>}
                      name="id_tipo" rules={[{required:true,message:"Selecione o motivo"}]}>
                      <Select placeholder="Selecione o tipo de falta...">
                        {tipos.map(t => <Option key={t.id} value={t.id}>{t.descricao}</Option>)}
                      </Select>
                    </Form.Item>

                    <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Observação</span>} name="obs">
                      <input placeholder="Descrição opcional..." style={{
                        width:"100%", padding:"8px 12px", borderRadius:8, fontSize:13,
                        border:"1px solid #D1D5DB", outline:"none", color:"#374151",
                        boxSizing:"border-box",
                      }}/>
                    </Form.Item>

                    <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Comprovativo (PDF)</span>}>
               <Upload {...uploadProps} accept=".pdf">
  <Button icon={<UploadOutlined/>}>
    Selecionar PDF
  </Button>
</Upload>
                    </Form.Item>

                    <Button
                      type="primary" block loading={submitting} onClick={submitJustificacao}
                      style={{
                        height:42, borderRadius:10, fontSize:14, fontWeight:700,
                        background:"linear-gradient(135deg,#185FA5,#2563EB)",
                        border:"none", letterSpacing:"0.2px",
                        boxShadow:"0 4px 14px rgba(24,95,165,0.35)",
                      }}
                    >
                      {submitting ? "A enviar..." : "Enviar justificação"}
                    </Button>
                  </Form>
                </div>
              )}
            </div>
          )}
        </Modal>
       <Modal
  open={modalFaltaOpen}
  onCancel={() => setModalFaltaOpen(false)}
  footer={null}
  width={520}
  title={null} // vamos fazer um cabeçalho personalizado
  closable
  maskClosable
>
  {faltaSelecionada && (
    <div style={{ padding: "8px 0 0" }}>
      {/* Cabeçalho personalizado */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, background: faltaSelecionada.justificado === "S" ? "#D1FAE5" : "#FEF3C7",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {faltaSelecionada.justificado === "S"
              ? <CheckCircleFilled style={{ fontSize: 22, color: "#065F46" }} />
              : <ExclamationCircleFilled style={{ fontSize: 22, color: "#92400E" }} />
            }
          </div>
          <div>
            <Typography.Title level={4} style={{ margin: 0, fontWeight: 700, color: "#111827" }}>
              Detalhes da Falta
            </Typography.Title>
            <Typography.Text style={{ fontSize: 13, color: "#6B7280" }}>
              #{faltaSelecionada.id}
            </Typography.Text>
          </div>
        </div>
        <Divider style={{ margin: "12px 0 0" }} />
      </div>

      {/* Corpo com as informações */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Bloco: Informações Gerais */}
        <div style={{
          background: "#F9FAFB", borderRadius: 12, padding: "16px 18px",
          border: "1px solid #F3F4F6",
        }}>
          <Typography.Text strong style={{ fontSize: 13, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 12 }}>
            Informações Gerais
          </Typography.Text>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <DetailRow label="Colaborador" icon={<UserOutlined />} value={faltaSelecionada.colaborador} />
            <DetailRow label="Motivo" icon={<FileTextOutlined />} value={faltaSelecionada.motivo || "—"} />
            <DetailRow label="Estado" icon={null}
              value={
                <Tag color={faltaSelecionada.justificado === "S" ? "success" : "gold"} style={{ fontWeight: 500 }}>
                  {faltaSelecionada.justificado === "S" ? "APROVADA" : "PENDENTE"}
                </Tag>
              }
            />
          </div>
        </div>

        {/* Bloco: Período */}
        <div style={{
          background: "#F9FAFB", borderRadius: 12, padding: "16px 18px",
          border: "1px solid #F3F4F6",
        }}>
          <Typography.Text strong style={{ fontSize: 13, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 12 }}>
            Período
          </Typography.Text>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <DetailRow label="Início" icon={<CalendarOutlined />} value={dayjs(faltaSelecionada.inicio).format("DD/MM/YYYY HH:mm")} />
            <DetailRow label="Fim" icon={<CalendarOutlined />} value={dayjs(faltaSelecionada.fim).format("DD/MM/YYYY HH:mm")} />
            <DetailRow label="Duração" icon={<ClockCircleOutlined />}
              value={`${dayjs(faltaSelecionada.fim).diff(dayjs(faltaSelecionada.inicio), 'hour')} horas`}
            />
          </div>
        </div>

        {/* Bloco: Aprovação (se aplicável) */}
        {(faltaSelecionada.justificador || faltaSelecionada.data_aprovacao) && (
          <div style={{
            background: "#F9FAFB", borderRadius: 12, padding: "16px 18px",
            border: "1px solid #F3F4F6",
          }}>
            <Typography.Text strong style={{ fontSize: 13, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 12 }}>
              Aprovação
            </Typography.Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {faltaSelecionada.justificador && (
                <DetailRow label="Justificado por" icon={<UserOutlined />} value={faltaSelecionada.justificador} />
              )}
              {faltaSelecionada.data_aprovacao && (
                <DetailRow label="Data da aprovação" icon={<CalendarOutlined />}
                  value={dayjs(faltaSelecionada.data_aprovacao).format("DD/MM/YYYY HH:mm")}
                />
              )}
            </div>
          </div>
        )}

        {/* Bloco: Observações */}
        {(faltaSelecionada.obs || faltaSelecionada.obs2) && (
          <div style={{
            background: "#F9FAFB", borderRadius: 12, padding: "16px 18px",
            border: "1px solid #F3F4F6",
          }}>
            <Typography.Text strong style={{ fontSize: 13, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 12 }}>
              Observações
            </Typography.Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {faltaSelecionada.obs && (
                <div>
                  <Typography.Text style={{ fontSize: 12, color: "#9CA3AF" }}>Colaborador:</Typography.Text>
                  <div style={{ fontSize: 13, color: "#374151", marginTop: 2, whiteSpace: "pre-wrap" }}>{faltaSelecionada.obs}</div>
                </div>
              )}
              {faltaSelecionada.obs2 && (
                <div style={{ marginTop: faltaSelecionada.obs ? 8 : 0 }}>
                  <Typography.Text style={{ fontSize: 12, color: "#9CA3AF" }}>Diretor:</Typography.Text>
                  <div style={{ fontSize: 13, color: "#B91C1C", marginTop: 2, whiteSpace: "pre-wrap" }}>{faltaSelecionada.obs2}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Comprovativo */}
        {faltaSelecionada.justificativo && (
          <div style={{ textAlign: "right" }}>
            <a
              href={faltaSelecionada.justificativo}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 13, fontWeight: 600, color: "#185FA5",
                background: "#EFF6FF", padding: "8px 16px", borderRadius: 8,
                textDecoration: "none", transition: "0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#DBEAFE"}
              onMouseLeave={e => e.currentTarget.style.background = "#EFF6FF"}
            >
              <FileTextOutlined /> Abrir comprovativo
            </a>
          </div>
        )}
      </div>
    </div>
  )}
</Modal>
      </Content>
    </Layout>
  );
};
const DetailRow = ({ label, value, icon }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    {icon && <span style={{ color: "#9CA3AF", fontSize: 14 }}>{icon}</span>}
    <span style={{ fontSize: 13, color: "#6B7280", minWidth: 120 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{value}</span>
  </div>
);

export default Transacoes;